/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import "moment/locale/th";

// FullCalendar components & plugins
import Fullcalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import LinearProgress from "@material-ui/core/LinearProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Divider from "@material-ui/core/Divider";

// @material-ui/icons
import DashboardIcon from "@material-ui/icons/Dashboard";
import EventNote from "@material-ui/icons/EventNote";
import AccessTime from "@material-ui/icons/AccessTime";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Warning from "@material-ui/icons/Warning";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Person from "@material-ui/icons/Person";
import Assignment from "@material-ui/icons/Assignment";
import PermContactCalendar from "@material-ui/icons/PermContactCalendar";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import FlightTakeoff from "@material-ui/icons/FlightTakeoff";
import LocalHospital from "@material-ui/icons/LocalHospital";
import Work from "@material-ui/icons/Work";
import TrendingUp from "@material-ui/icons/TrendingUp";
import ArrowForward from "@material-ui/icons/ArrowForward";
import HelpOutline from "@material-ui/icons/HelpOutline";
import Timer from "@material-ui/icons/Timer";
import Today from "@material-ui/icons/Today";
import Close from "@material-ui/icons/Close";

// Core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import Button from "components/CustomButtons/Button.js";
import TSSelect2 from "components/Forms/TSSelect2";

const useStyles = makeStyles((theme) => ({
  cardCategoryWhite: {
    color: "rgba(255,255,255,.8)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "500",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none",
  },
  statsCategory: {
    color: "#777",
    margin: "0",
    fontSize: "13px",
    fontWeight: "bold",
  },
  statsTitle: {
    color: "#3C4858",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    fontSize: "26px",
    marginBottom: "3px",
  },
  statsUnit: {
    fontSize: "16px",
    fontWeight: "normal",
    color: "#888",
    marginLeft: "4px",
  },
  userProfileCard: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap",
    padding: "10px",
  },
  userAvatar: {
    width: theme.spacing(9),
    height: theme.spacing(9),
    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
    border: "2px solid #fff",
  },
  tableResponsive: {
    width: "100%",
    overflowX: "auto",
  },
  sectionTitle: {
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "1.25rem",
    marginBottom: "12px",
  },
  fiscalBadge: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: "bold",
    padding: "4px 12px",
    borderRadius: "16px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    border: "1px solid #c8e6c9",
  },
  statusPending: {
    backgroundColor: "#ff9800",
    color: "#fff",
    fontWeight: "bold",
  },
  statusApproved: {
    backgroundColor: "#4caf50",
    color: "#fff",
    fontWeight: "bold",
  },
  statusRejected: {
    backgroundColor: "#f44336",
    color: "#fff",
    fontWeight: "bold",
  },
  statusCancelled: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
    fontWeight: "bold",
  },
  progressContainer: {
    marginTop: "8px",
    marginBottom: "4px",
  },
  calendarCard: {
    "& .fc": {
      fontFamily: '"Sarabun", sans-serif',
    },
    "& .fc-toolbar-title": {
      fontSize: "1.25rem !important",
      fontWeight: "bold",
      color: "#333",
    },
    "& .fc-button-primary": {
      backgroundColor: "#1976d2 !important",
      borderColor: "#1976d2 !important",
      textTransform: "capitalize",
      fontWeight: "bold",
      boxShadow: "none !important",
      "&:hover": {
        backgroundColor: "#1565c0 !important",
      },
      "&:focus": {
        boxShadow: "none !important",
      },
    },
    "& .fc-button-active": {
      backgroundColor: "#0d47a1 !important",
      borderColor: "#0d47a1 !important",
    },
    "& .fc-event": {
      borderRadius: "4px",
      padding: "2px 4px",
      fontSize: "12px",
      cursor: "pointer",
      border: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      transition: "transform 0.15s ease",
      "&:hover": {
        transform: "scale(1.02)",
        filter: "brightness(0.95)",
      },
    },
    "& .fc-day-today": {
      backgroundColor: "rgba(25, 118, 210, 0.06) !important",
    },
    "& .fc-col-header-cell": {
      backgroundColor: "#f5f7fa",
      padding: "8px 0",
      fontWeight: "bold",
      color: "#555",
    },
  },
  legendContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#fafafa",
    borderRadius: "6px",
    marginBottom: "16px",
    alignItems: "center",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: "500",
    color: "#444",
  },
  legendDot: {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    display: "inline-block",
  },
}));

const HRDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const calendarRef = useRef(null);

  // 1. คำนวณปีงบประมาณปัจจุบัน (เริ่มต้น)
  // เงื่อนไข: 1 ต.ค. ปี X ถึง 30 ก.ย. ปี X+1 คือ ปีงบประมาณ X+1
  let currentThaiYear = parseInt(moment().format("BBBB"));
  if (isNaN(currentThaiYear)) {
    currentThaiYear = parseInt(moment().format("YYYY")) + 543;
  }
  const currentFiscalYear = moment().month() >= 9 ? currentThaiYear + 1 : currentThaiYear;

  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ข้อมูลสถิติการลา
  const [leaveAccrued, setLeaveAccrued] = useState(null);
  const [leaveLimitMax, setLeaveLimitMax] = useState(10);
  const [leaveList, setLeaveList] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    sickDays: 0,
    sickCount: 0,
    businessDays: 0,
    businessCount: 0,
    vacationUsed: 0,
    vacationCount: 0,
    otherDays: 0,
    otherCount: 0,
    totalDays: 0,
    totalCount: 0,
  });

  // ข้อมูลสถิติการขอลงเวลาปฏิบัติราชการ
  const [categories, setCategories] = useState([]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState({
    total: 0,
    type1: 0, // กจ. 1 ลืมเข้า
    type2: 0, // กจ. 4 ลืมออก
    type3: 0, // กจ. 1 & 4 ลืมเข้าและออก
    approved: 0,
    pending: 0,
    rejected: 0,
    cancelled: 0,
  });

  // รายการรอดำเนินการรับรอง/อนุมัติ
  const [myApproveCount, setMyApproveCount] = useState(0);

  // Modal แสดงรายละเอียด Event ที่ถูกคลิกในปฏิทิน
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // รายการตัวเลือกปีงบประมาณ
  const fiscalYearOptions = [
    { value: currentFiscalYear + 1, label: `ปีงบประมาณ ${currentFiscalYear + 1}` },
    { value: currentFiscalYear, label: `ปีงบประมาณ ${currentFiscalYear}` },
    { value: currentFiscalYear - 1, label: `ปีงบประมาณ ${currentFiscalYear - 1}` },
    { value: currentFiscalYear - 2, label: `ปีงบประมาณ ${currentFiscalYear - 2}` },
    { value: currentFiscalYear - 3, label: `ปีงบประมาณ ${currentFiscalYear - 3}` },
    { value: currentFiscalYear - 4, label: `ปีงบประมาณ ${currentFiscalYear - 4}` },
  ];

  // โหลดข้อมูล User Profile
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("currentUser") || "{}");
      setCurrentUser(user);
    } catch (e) {
      console.log(e);
    }
  }, []);

  // โหลดข้อมูลสถิติต่างๆ เมื่อเปลี่ยนปีงบประมาณ
  useEffect(() => {
    if (!currentUser?.userName) return;
    setLoading(true);

    const userName = currentUser.userName;

    // 1. ดึงข้อมูลวันลาสะสม / สิทธิวันลาประจำปีงบประมาณ
    const fetchAccrued = axios
      .get(`/api/v1/leave/accrued/${userName}/${fiscalYear}`)
      .then((res) => {
        if (res.data?.data) {
          setLeaveAccrued(res.data.data);
        } else {
          setLeaveAccrued(null);
        }
        if (res.data?.maxLeave) {
          setLeaveLimitMax(res.data.maxLeave);
        }
      })
      .catch((err) => console.log(err));

    // ดึงโควตาสิทธิวันลาพักผ่อนประจำปี (LeaveLimit สำหรับ LeaveTypeKey=3)
    const typeGovKey = currentUser.typeGovKey || 1;
    const fetchLimit = axios
      .post("/api/v1/leave/limit", { LeaveTypeKey: 3, TypeGovKey: typeGovKey })
      .then((res) => {
        const limits = res.data?.data || [];
        const vacationLimit = limits.find(
          (l) => l.leaveTypeKey === 3 && (l.typeGovKey === typeGovKey || !l.typeGovKey)
        );
        if (vacationLimit?.maxLeave) {
          setLeaveLimitMax(vacationLimit.maxLeave);
        }
      })
      .catch((err) => console.log(err));

    // 2. ดึงประวัติการลาประจำปีงบประมาณ
    const fetchLeaves = axios
      .get(`/api/v1/leave/myheader?Calyear=${fiscalYear}`)
      .then((res) => {
        const rawLeaves = res.data || [];
        // กรองเฉพาะการลาที่อยู่ในช่วงปีงบประมาณ fiscalYear (1 ต.ค. (FY-1) ถึง 30 ก.ย. FY)
        const fyStart = moment(`${fiscalYear - 544}-10-01`, "YYYY-MM-DD");
        const fyEnd = moment(`${fiscalYear - 543}-09-30 23:59:59`, "YYYY-MM-DD HH:mm:ss");

        const leaves = rawLeaves.filter((l) => {
          if (!l.startDate) return true;
          const s = moment(l.startDate);
          return s.isSameOrAfter(fyStart) && s.isSameOrBefore(fyEnd);
        });

        setLeaveList(leaves);

        let sDays = 0, sCount = 0;
        let bDays = 0, bCount = 0;
        let vDays = 0, vCount = 0;
        let oDays = 0, oCount = 0;
        let tDays = 0, tCount = 0;

        leaves.forEach((l) => {
          if (!l.isCancel) {
            const days = l.totalDate || 0;
            tDays += days;
            tCount += 1;

            if (l.leaveTypeKey === 1) {
              sDays += days;
              sCount += 1;
            } else if (l.leaveTypeKey === 2) {
              bDays += days;
              bCount += 1;
            } else if (l.leaveTypeKey === 3) {
              vDays += days;
              vCount += 1;
            } else {
              oDays += days;
              oCount += 1;
            }
          }
        });

        setLeaveStats({
          sickDays: sDays,
          sickCount: sCount,
          businessDays: bDays,
          businessCount: bCount,
          vacationUsed: vDays,
          vacationCount: vCount,
          otherDays: oDays,
          otherCount: oCount,
          totalDays: tDays,
          totalCount: tCount,
        });
      })
      .catch((err) => console.log(err));

    // 3. ดึงประวัติการขอลงเวลา (กจ. 1 / กจ. 4) ประจำปีงบประมาณ
    const fetchAttendance = axios
      .get(`/api/v1/timeattendance/myheader?FiscalYear=${fiscalYear}`)
      .then((res) => {
        const attList = res.data?.data || [];
        setAttendanceList(attList);

        let t1 = 0, t2 = 0, t3 = 0;
        let app = 0, pend = 0, rej = 0, can = 0;

        attList.forEach((a) => {
          if (a.attendanceType === 1) t1 += 1;
          else if (a.attendanceType === 2) t2 += 1;
          else if (a.attendanceType === 3) t3 += 1;

          if (a.isCancel) {
            can += 1;
          } else if (a.requestStatusKey === 2 || a.isApprove === true) {
            app += 1;
          } else if (a.requestStatusKey === -1 || a.isApprove === false) {
            rej += 1;
          } else {
            pend += 1;
          }
        });

        setAttendanceStats({
          total: attList.length,
          type1: t1,
          type2: t2,
          type3: t3,
          approved: app,
          pending: pend,
          rejected: rej,
          cancelled: can,
        });
      })
      .catch((err) => console.log(err));

    // 4. ดึงหมวดหมู่ประเภทการลงเวลา (Active Categories)
    const fetchCategories = axios
      .get("/api/v1/timeattendance/categories")
      .then((res) => {
        if (res.data?.data) {
          setCategories(res.data.data);
        }
      })
      .catch((err) => console.log(err));

    // 5. ดึงจำนวนรายการรอรับรอง/อนุมัติ
    const fetchMyApprove = axios
      .get("/api/v1/timeattendance/myapprove")
      .then((res) => {
        const list = res.data?.data || [];
        setMyApproveCount(list.length);
      })
      .catch((err) => console.log(err));

    Promise.all([fetchAccrued, fetchLimit, fetchLeaves, fetchAttendance, fetchCategories, fetchMyApprove]).finally(() => {
      setLoading(false);
    });
  }, [fiscalYear, currentUser]);

  // คำนวณวันลาพักผ่อน
  // วันลาทั้งหมด = LeaveLimit.MaxLeave (สิทธิประจำปี) + LeaveAccrued.AccruedValue (สะสมยกมา)
  const maxVacationThisYear = leaveLimitMax || 10;
  const accruedFromLastYear = leaveAccrued?.accruedValue || 0;
  const accruedTotal = maxVacationThisYear + accruedFromLastYear;
  const vacationUsed = leaveStats.vacationUsed;
  const vacationRemaining = Math.max(0, accruedTotal - vacationUsed);
  const vacationPercentUsed = accruedTotal > 0 ? Math.min(100, Math.round((vacationUsed / accruedTotal) * 100)) : 0;

  // คำนวณสถิติแยกตามหมวดหมู่แบบ dynamic
  const categoryStatsList = React.useMemo(() => {
    const catMap = new Map();

    // 1. นำหมวดหมู่ทั้งหมดจาก API มาตั้งต้น
    categories.forEach((cat) => {
      catMap.set(cat.categoryID, {
        categoryID: cat.categoryID,
        categoryName: cat.categoryName,
        categoryMode: cat.categoryMode,
        maxHoursPerRequest: cat.maxHoursPerRequest,
        quotaLimitType: cat.quotaLimitType,
        quotaMaxCount: cat.quotaMaxCount,
        count: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        totalHours: 0,
      });
    });

    // 2. นำข้อมูลจาก attendanceList มานับสถิติ
    attendanceList.forEach((a) => {
      let targetCatId = a.categoryID;
      if (!targetCatId && a.attendanceType) {
        const found = categories.find((c) => c.categoryMode === a.attendanceType);
        if (found) targetCatId = found.categoryID;
      }

      if (targetCatId && catMap.has(targetCatId)) {
        const item = catMap.get(targetCatId);
        item.count += 1;
        if (a.durationHours) item.totalHours += a.durationHours;
        if (a.isCancel) {
          // ยกเลิก
        } else if (a.requestStatusKey === 2 || a.isApprove === true) {
          item.approved += 1;
        } else if (a.requestStatusKey === -1 || a.isApprove === false) {
          item.rejected += 1;
        } else {
          item.pending += 1;
        }
      } else {
        const vId = targetCatId || `type_${a.attendanceType}`;
        if (!catMap.has(vId)) {
          catMap.set(vId, {
            categoryID: vId,
            categoryName: a.category?.categoryName || getAttendanceTypeText(a.attendanceType),
            categoryMode: a.category?.categoryMode || a.attendanceType,
            maxHoursPerRequest: a.category?.maxHoursPerRequest || null,
            quotaLimitType: a.category?.quotaLimitType || 0,
            quotaMaxCount: a.category?.quotaMaxCount || null,
            count: 0,
            approved: 0,
            pending: 0,
            rejected: 0,
            totalHours: 0,
          });
        }
        const item = catMap.get(vId);
        item.count += 1;
        if (a.durationHours) item.totalHours += a.durationHours;
        if (a.isCancel) {
          // ยกเลิก
        } else if (a.requestStatusKey === 2 || a.isApprove === true) {
          item.approved += 1;
        } else if (a.requestStatusKey === -1 || a.isApprove === false) {
          item.rejected += 1;
        } else {
          item.pending += 1;
        }
      }
    });

    return Array.from(catMap.values());
  }, [categories, attendanceList]);

  const getCategoryColor = (cat, index = 0) => {
    if (cat.categoryMode === 1) return "info";
    if (cat.categoryMode === 2) return "warning";
    if (cat.categoryMode === 3) return "rose";
    const quotaColors = ["success", "primary", "info", "warning", "rose"];
    return quotaColors[index % quotaColors.length];
  };

  const getCategoryIcon = (cat) => {
    if (cat.categoryMode === 1) return <Timer />;
    if (cat.categoryMode === 2) return <AccessTime />;
    if (cat.categoryMode === 3) return <ErrorOutline />;
    const name = cat.categoryName || "";
    if (name.includes("ทันตกรรม") || name.includes("แพทย์")) return <LocalHospital />;
    if (name.includes("กิจธุระ") || name.includes("งาน")) return <Work />;
    return <CheckCircle />;
  };

  const getQuotaDescription = (cat) => {
    if (cat.categoryMode !== 10) return "ขอรับรองเวลาปฏิบัติงานจริง";
    let desc = [];
    if (cat.maxHoursPerRequest) {
      desc.push(`สิทธิ์ ${cat.maxHoursPerRequest} ชม./ครั้ง`);
    }
    if (cat.quotaMaxCount && cat.quotaLimitType > 0) {
      const typeLabels = {
        1: "สัปดาห์",
        2: "เดือน",
        3: "ครึ่งปี",
        4: "ปีงบประมาณ",
      };
      desc.push(`ไม่เกิน ${cat.quotaMaxCount} ครั้ง/${typeLabels[cat.quotaLimitType] || "รอบ"}`);
    }
    return desc.join(" • ") || "ขอตามโควต้า";
  };

  // =========================================================
  // แปลงข้อมูลการลา และการขอลงเวลา เป็น FullCalendar Events
  // =========================================================
  const calendarEvents = [];

  // 1. Events การลา (แท่งยาวได้หลายวัน: start ถึง end + 1 day ตาม FullCalendar standard)
  leaveList.forEach((l) => {
    let typeName = "การลา";
    let bgColor = "#5c6bc0"; // ค่าเริ่มต้น: น้ำเงินอมม่วง

    if (l.leaveTypeKey === 1) {
      typeName = "ลาป่วย";
      bgColor = "#e53935"; // แดง
    } else if (l.leaveTypeKey === 2) {
      typeName = "ลากิจ";
      bgColor = "#fb8c00"; // ส้ม
    } else if (l.leaveTypeKey === 3) {
      typeName = "ลาพักผ่อน";
      bgColor = "#43a047"; // เขียว
    } else if (l.leaveTypeName) {
      typeName = l.leaveTypeName;
    }

    if (l.isCancel) {
      bgColor = "#9e9e9e"; // สีเทาถ้ายกเลิก
    }

    const startDateStr = moment(l.startDate).format("YYYY-MM-DD");
    // FullCalendar all-day event multi-day end date เป็น exclusive จึงต้อง +1 วัน เพื่อให้แท่งยาวครอบคลุมวันสุดท้าย
    const endDateStr = l.endDate
      ? moment(l.endDate).add(1, "days").format("YYYY-MM-DD")
      : moment(l.startDate).add(1, "days").format("YYYY-MM-DD");

    const reasonText = l.title || l.leaveDesc || "";
    const titleText = `[${typeName}] ${reasonText ? reasonText + " " : ""}(${l.totalDate} วัน)`;

    calendarEvents.push({
      id: `leave_${l.leaveRequestID}`,
      title: titleText,
      start: startDateStr,
      end: endDateStr,
      allDay: true,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor: "#ffffff",
      extendedProps: {
        category: "leave",
        raw: l,
        typeName: typeName,
        totalDate: l.totalDate,
        startDate: l.startDate,
        endDate: l.endDate,
        reason: reasonText,
        isApprove: l.isApprove,
        isCancel: l.isCancel,
      },
    });
  });

  // 2. Events การขอลงเวลาปฏิบัติราชการ (วันละแท่ง Single-day event)
  attendanceList.forEach((a) => {
    let typeName = a.category?.categoryName || "ลงเวลา";
    let bgColor = "#0288d1"; // ฟ้า

    if (a.category?.categoryMode === 10 || a.attendanceType === 10) {
      typeName = a.category?.categoryName || "ขอตามโควต้า";
      bgColor = "#2e7d32"; // เขียว
    } else if (a.attendanceType === 1) {
      typeName = a.category?.categoryName || "กจ. 1 (ลืมเข้า)";
      bgColor = "#0288d1"; // ฟ้าสด
    } else if (a.attendanceType === 2) {
      typeName = a.category?.categoryName || "กจ. 4 (ลืมออก)";
      bgColor = "#8e24aa"; // ม่วง
    } else if (a.attendanceType === 3) {
      typeName = a.category?.categoryName || "กจ. 1/4 (ลืมเข้า-ออก)";
      bgColor = "#d81b60"; // ชมพูบานเย็น
    } else if (a.category?.categoryName) {
      typeName = a.category.categoryName;
      bgColor = "#2e7d32";
    }

    if (a.isCancel) {
      bgColor = "#9e9e9e";
    }

    const targetDate = a.workDate || a.checkInDT || a.checkOutDT;
    const dateStr = moment(targetDate).format("YYYY-MM-DD");

    let timeDetail = "";
    if (a.durationHours) {
      timeDetail = ` (${a.durationHours} ชม.)`;
    } else if (a.checkInDT && a.attendanceType === 1) {
      timeDetail = ` (${moment(a.checkInDT).format("HH:mm")})`;
    } else if (a.checkOutDT && a.attendanceType === 2) {
      timeDetail = ` (${moment(a.checkOutDT).format("HH:mm")})`;
    } else if (a.checkInDT && a.checkOutDT) {
      timeDetail = ` (${moment(a.checkInDT).format("HH:mm")}-${moment(a.checkOutDT).format("HH:mm")})`;
    }

    const titleText = `[${typeName}]${timeDetail}`;

    calendarEvents.push({
      id: `att_${a.timeAttendanceRequestID}`,
      title: titleText,
      start: dateStr,
      end: dateStr,
      allDay: true,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor: "#ffffff",
      extendedProps: {
        category: "attendance",
        raw: a,
        typeName: typeName,
        attendanceType: a.attendanceType,
        workDate: targetDate,
        reason: a.reason,
        checkInDT: a.checkInDT,
        checkOutDT: a.checkOutDT,
        requestStatusKey: a.requestStatusKey,
        isApprove: a.isApprove,
        isCancel: a.isCancel,
        id: a.timeAttendanceRequestID,
      },
    });
  });

  // จัดการเมื่อคลิก Event บนปฏิทิน
  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    if (props.category === "attendance" && props.id) {
      // นำทางไปยังหน้ารายละเอียดคำขอลงเวลาทันที
      history.push(`/hr/attendance/request/${props.id}`);
    } else {
      // เปิด Dialog แสดงรายละเอียดของ Event
      setSelectedEvent({
        title: info.event.title,
        ...props,
      });
      setModalOpen(true);
    }
  };

  // Render Status Badge สำหรับตาราง
  const renderAttendanceStatus = (item) => {
    if (item.isCancel) {
      return <Chip label="ยกเลิกแล้ว" className={classes.statusCancelled} size="small" />;
    }
    if (item.requestStatusKey === 2 || item.isApprove === true) {
      return <Chip label="อนุมัติสมบูรณ์" className={classes.statusApproved} size="small" />;
    }
    if (item.requestStatusKey === -1 || item.isApprove === false) {
      return <Chip label="ไม่อนุมัติ / ปฏิเสธ" className={classes.statusRejected} size="small" />;
    }
    return <Chip label="รอดำเนินการ" className={classes.statusPending} size="small" />;
  };

  const getAttendanceTypeText = (type) => {
    if (type === 1) return "ลืมลงเวลาเข้า (กจ. 1)";
    if (type === 2) return "ลืมลงเวลาออก (กจ. 4)";
    if (type === 3) return "ทั้งเข้าและออก (กจ. 1 / กจ. 4)";
    if (type === 10) return "ขอลงเวลาตามโควต้า";
    return "-";
  };

  return (
    <div>
      {/* 1. Header Profile & Fiscal Year Filter Card */}
      <Card style={{ marginTop: "0px", marginBottom: "20px" }}>
        <CardBody style={{ padding: "18px 24px" }}>
          <GridContainer alignItems="center" justify="space-between">
            {/* ฝั่งซ้าย: ข้อมูลผู้ใช้งาน */}
            <GridItem xs={12} sm={12} md={7}>
              <div className={classes.userProfileCard}>
                <Avatar
                  alt={currentUser?.userName}
                  src={`https://e.trangskin.go.th/avatar/${currentUser?.userName}.jpg`}
                  className={classes.userAvatar}
                />
                <div>
                  <Typography variant="h5" style={{ fontWeight: "bold", color: "#333", fontFamily: '"Sarabun", sans-serif' }}>
                    {currentUser?.fullName || `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: "2px" }}>
                    <i className="fa fa-id-card mr-1 text-primary"></i> รหัส: <strong>{currentUser?.userName}</strong> | {currentUser?.positionName || "เจ้าหน้าที่"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: "2px" }}>
                    <i className="fa fa-hospital mr-1 text-info"></i> {currentUser?.userDepartment?.[0]?.department?.name || "โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้"}
                  </Typography>
                </div>
              </div>
            </GridItem>

            {/* ฝั่งขวา: เลือกปีงบประมาณ และป้ายแสดงช่วงเวลา */}
            <GridItem xs={12} sm={12} md={5} style={{ textAlign: "right" }}>
              <div style={{ display: "inline-block", textAlign: "left", minWidth: "260px" }}>
                <TSSelect2
                  label="เลือกปีงบประมาณ"
                  options={fiscalYearOptions}
                  value={fiscalYear}
                  onChange={(e) => setFiscalYear(e.value)}
                />
                <div style={{ marginTop: "8px" }}>
                  <span className={classes.fiscalBadge}>
                    <i className="fa fa-calendar-alt"></i>
                    1 ต.ค. {fiscalYear - 1} – 30 ก.ย. {fiscalYear}
                  </span>
                </div>
              </div>
            </GridItem>
          </GridContainer>

          {/* เมนูทางลัด (Quick Actions) */}
          <div style={{ borderTop: "1px solid #eee", marginTop: "15px", paddingTop: "12px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button
              color="info"
              size="sm"
              onClick={() => history.push("/leave/request")}
            >
              <i className="fa fa-plane-departure mr-1"></i> เขียนใบลา
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={() => history.push("/hr/attendance/request")}
            >
              <i className="fa fa-plus-circle mr-1"></i> ขอลงเวลาปฏิบัติราชการ
            </Button>
            <Button
              color="white"
              size="sm"
              onClick={() => history.push("/leave/me")}
              style={{ color: "#333" }}
            >
              <i className="fa fa-calendar-check mr-1"></i> ประวัติการลา
            </Button>
            <Button
              color="white"
              size="sm"
              onClick={() => history.push("/hr/attendance/me")}
              style={{ color: "#333" }}
            >
              <i className="fa fa-history mr-1"></i> ประวัติการลงเวลาปฏิบัติราชการ
            </Button>
      
          </div>

          
        </CardBody>
      </Card>

      {/* แจ้งเตือน: รายการรอคุณรับรอง/อนุมัติ (ถ้ามี) */}
      {myApproveCount > 0 && (
        <GridContainer>
          <GridItem xs={12}>
            <div
              style={{
                background: "linear-gradient(60deg, #ffa726, #fb8c00)",
                color: "#fff",
                borderRadius: "6px",
                padding: "12px 20px",
                marginBottom: "20px",
                boxShadow: "0 4px 20px 0 rgba(0, 0, 0,.14), 0 7px 10px -5px rgba(255, 152, 0,.4)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <i className="fa fa-bell fa-2x"></i>
                <div>
                  <h4 style={{ margin: 0, fontWeight: "bold", color: "#fff" }}>
                    คุณมีรายการขอลงเวลารอการรับรอง / อนุมัติ ({myApproveCount} รายการ)
                  </h4>
                  <small style={{ color: "#fff", opacity: 0.9 }}>
                    มีเพื่อนร่วมงานระบุให้ท่านเป็นพยาน หรือรอผู้บังคับบัญชาพิจารณาเห็นชอบ
                  </small>
                </div>
              </div>
              <Button
                color="white"
                size="sm"
                onClick={() => history.push("/hr/attendance/myapprove")}
                style={{ color: "#e65100", fontWeight: "bold" }}
              >
                ตรวจสอบทันที <ArrowForward style={{ fontSize: "16px", marginLeft: "4px" }} />
              </Button>
            </div>
          </GridItem>
        </GridContainer>
      )}

      {/* ========================================================= */}
      {/* 2. สถิติการลาประจำปีงบประมาณ (Leave Statistics) */}
      {/* ========================================================= */}
      <div className={classes.sectionTitle}>
        <FlightTakeoff style={{ color: "#e91e63" }} />
        <span>สถิติการลาของฉัน (ปีงบประมาณ {fiscalYear})</span>
      </div>

      <GridContainer>
        {/* การ์ด 1: สิทธิวันลาพักผ่อน & วันลาคงเหลือ */}
        <GridItem xs={12} sm={6} md={3}>
          <Card>
            <CardHeader color="success" stats icon>
              <CardIcon color="success">
                <EventNote />
              </CardIcon>
              <p className={classes.statsCategory}>ลาพักผ่อน</p>
              <h3 className={classes.statsTitle}>
                {vacationUsed} จาก {accruedTotal}
                <span className={classes.statsUnit}>วัน</span>
              </h3>
            </CardHeader>
            <CardBody style={{ paddingTop: "0px" }}>
              <div className={classes.progressContainer}>
                <LinearProgress
                  variant="determinate"
                  value={vacationPercentUsed}
                  style={{ height: "6px", borderRadius: "3px", backgroundColor: "#c8e6c9" }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#666" }}>
                <span>ใช้ไป {vacationUsed} วัน ({leaveStats.vacationCount} ครั้ง)</span>
                <span>คงเหลือ {vacationRemaining} วัน (สิทธิรวม {accruedTotal} วัน)</span>
              </div>
            </CardBody>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-info-circle mr-1 text-success"></i>
                สิทธิปีนี้ {maxVacationThisYear} วัน + สะสมยกมา {accruedFromLastYear} วัน (รวม {accruedTotal} วัน)
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        {/* การ์ด 2: ลาป่วย */}
        <GridItem xs={12} sm={6} md={3}>
          <Card>
            <CardHeader color="danger" stats icon>
              <CardIcon color="danger">
                <LocalHospital />
              </CardIcon>
              <p className={classes.statsCategory}>ลาป่วย (สะสมปีนี้)</p>
              <h3 className={classes.statsTitle}>
                {leaveStats.sickDays}
                <span className={classes.statsUnit}>วัน</span>
              </h3>
            </CardHeader>
            <CardBody style={{ paddingTop: "0px" }}>
              <Typography variant="body2" color="textSecondary">
                จำนวนครั้งที่ลาป่วย: <strong>{leaveStats.sickCount}</strong> ครั้ง
              </Typography>
            </CardBody>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-heartbeat mr-1 text-danger"></i>
                ลาป่วยตามระเบียบราชการ
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        {/* การ์ด 3: ลากิจส่วนตัว */}
        <GridItem xs={12} sm={6} md={3}>
          <Card>
            <CardHeader color="warning" stats icon>
              <CardIcon color="warning">
                <Work />
              </CardIcon>
              <p className={classes.statsCategory}>ลากิจส่วนตัว</p>
              <h3 className={classes.statsTitle}>
                {leaveStats.businessDays}
                <span className={classes.statsUnit}>วัน</span>
              </h3>
            </CardHeader>
            <CardBody style={{ paddingTop: "0px" }}>
              <Typography variant="body2" color="textSecondary">
                จำนวนครั้งที่ลากิจ: <strong>{leaveStats.businessCount}</strong> ครั้ง
              </Typography>
            </CardBody>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-briefcase mr-1 text-warning"></i>
                ลากิจส่วนตัว / กิจจำเป็น
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        {/* การ์ด 4: รวมวันลาทุกประเภท */}
        <GridItem xs={12} sm={6} md={3}>
          <Card>
            <CardHeader color="info" stats icon>
              <CardIcon color="info">
                <TrendingUp />
              </CardIcon>
              <p className={classes.statsCategory}>รวมวันลาทุกประเภท</p>
              <h3 className={classes.statsTitle}>
                {leaveStats.totalDays}
                <span className={classes.statsUnit}>วัน</span>
              </h3>
            </CardHeader>
            <CardBody style={{ paddingTop: "0px" }}>
              <Typography variant="body2" color="textSecondary">
                รวมทั้งหมด <strong>{leaveStats.totalCount}</strong> ครั้ง ในปีงบฯ {fiscalYear}
              </Typography>
            </CardBody>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                <i className="fa fa-check-circle mr-1 text-info"></i>
                รวมป่วย กิจ พักผ่อน และอื่นๆ
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ========================================================= */}
      {/* 3. สถิติการขอลงเวลาปฏิบัติราชการ (Time Attendance) */}
      {/* ========================================================= */}
      <div className={classes.sectionTitle} style={{ marginTop: "20px" }}>
        <AccessTime style={{ color: "#00acc1" }} />
        <span>สถิติการขอลงเวลาปฏิบัติราชการ (ปีงบประมาณ {fiscalYear})</span>
      </div>

      <GridContainer>
        {/* รวมคำขอทั้งหมด */}
        <GridItem xs={12} sm={6} md={3}>
          <Card>
            <CardHeader color="primary" stats icon>
              <CardIcon color="primary">
                <Assignment />
              </CardIcon>
              <p className={classes.statsCategory}>คำขอลงเวลาทั้งหมด</p>
              <h3 className={classes.statsTitle}>
                {attendanceStats.total}
                <span className={classes.statsUnit}>ครั้ง</span>
              </h3>
            </CardHeader>
            <CardBody style={{ paddingTop: "0px" }}>
              <Typography variant="body2" style={{ color: "#4caf50", fontWeight: "bold" }}>
                <i className="fa fa-check mr-1"></i> อนุมัติแล้ว: {attendanceStats.approved} ครั้ง
              </Typography>
            </CardBody>
            <CardFooter stats>
              <div style={{ fontSize: "12px", color: "#888" }}>
                รอดำเนินการ: {attendanceStats.pending} | ปฏิเสธ: {attendanceStats.rejected}
              </div>
            </CardFooter>
          </Card>
        </GridItem>

        {/* การ์ดสถิติตามประเภท / หมวดหมู่การลงเวลาแบบ dynamic */}
        {categoryStatsList.map((cat, idx) => (
          <GridItem xs={12} sm={6} md={3} key={cat.categoryID || idx}>
            <Card>
              <CardHeader color={getCategoryColor(cat, idx)} stats icon>
                <CardIcon color={getCategoryColor(cat, idx)}>
                  {getCategoryIcon(cat)}
                </CardIcon>
                <p
                  className={classes.statsCategory}
                  style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={cat.categoryName}
                >
                  {cat.categoryName}
                </p>
                <h3 className={classes.statsTitle}>
                  {cat.count}
                  <span className={classes.statsUnit}>ครั้ง</span>
                </h3>
              </CardHeader>
              <CardBody style={{ paddingTop: "0px" }}>
                <Typography variant="body2" style={{ fontWeight: "500", color: cat.approved > 0 ? "#2e7d32" : "#666" }}>
                  <i className="fa fa-check-circle mr-1"></i>
                  อนุมัติ: <strong>{cat.approved}</strong> ครั้ง
                  {cat.categoryMode === 10 && cat.totalHours > 0 ? ` (${cat.totalHours} ชม.)` : ""}
                </Typography>
              </CardBody>
              <CardFooter stats>
                <div
                  style={{ fontSize: "12px", color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={getQuotaDescription(cat)}
                >
                  {getQuotaDescription(cat)}
                </div>
              </CardFooter>
            </Card>
          </GridItem>
        ))}
      </GridContainer>

      {/* ========================================================= */}
      {/* 4. ปฏิทินแสดงการลาและการขอลงเวลา (Calendar View) */}
      {/* ========================================================= */}
      <GridContainer style={{ marginTop: "20px" }}>
        <GridItem xs={12}>
          <Card className={classes.calendarCard}>
            <CardHeader color="primary">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <Today style={{ verticalAlign: "middle", marginRight: "8px" }} />
                    ปฏิทินสถิติการลาและการขอลงเวลา
                  </h4>
                  <p className={classes.cardCategoryWhite}>
                    แสดงแถบช่วงวันลาและรายการลงเวลาปฏิบัติราชการ ประจำปีงบประมาณ {fiscalYear}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Legend คำอธิบายสี */}
              <div className={classes.legendContainer}>
                <span style={{ fontWeight: "bold", fontSize: "13px", color: "#333", marginRight: "8px" }}>
                  <i className="fa fa-tags mr-1 text-primary"></i> สัญลักษณ์สี:
                </span>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#43a047" }}></span>
                  <span>ลาพักผ่อน (แถบยาวหลายวัน)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#e53935" }}></span>
                  <span>ลาป่วย</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#fb8c00" }}></span>
                  <span>ลากิจส่วนตัว</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#0288d1" }}></span>
                  <span>กจ. 1 (ลืมเข้า)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#8e24aa" }}></span>
                  <span>กจ. 4 (ลืมออก)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#d81b60" }}></span>
                  <span>กจ. 1/4 (ลืมเข้า-ออก)</span>
                </div>
                <div className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: "#2e7d32" }}></span>
                  <span>ขอตามโควต้า</span>
                </div>
              </div>

              {/* ปฏิทิน FullCalendar */}
              <div style={{ minHeight: "550px" }}>
                <Fullcalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,listMonth",
                  }}
                  locale="th"
                  events={calendarEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  displayEventTime={false}
                  buttonText={{
                    today: "วันนี้",
                    month: "รายเดือน",
                    list: "รายการ",
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ========================================================= */}
      {/* 5. ตารางประวัติรายการล่าสุดในปีงบประมาณ */}
      {/* ========================================================= */}
      <GridContainer style={{ marginTop: "10px" }}>
        {/* รายการลงเวลาปฏิบัติราชการล่าสุด */}
        <GridItem xs={12} sm={12} md={6}>
          <Card>
            <CardHeader color="info">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <i className="fa fa-clock mr-2"></i> รายการลงเวลาปฏิบัติราชการล่าสุด
                  </h4>
                  <p className={classes.cardCategoryWhite}>ปีงบประมาณ {fiscalYear}</p>
                </div>
                <Button
                  color="transparent"
                  size="sm"
                  onClick={() => history.push("/hr/attendance/me")}
                  style={{ color: "#fff", border: "1px solid rgba(255,255,255,0.5)" }}
                >
                  ดูทั้งหมด
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <div className={classes.tableResponsive}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold" }}>วันที่ปฏิบัติงาน</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>ประเภท</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>สถานะ</TableCell>
                      <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>จัดการ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendanceList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" style={{ color: "#888", padding: "20px" }}>
                          ไม่มีประวัติขอลงเวลาในปีงบประมาณนี้
                        </TableCell>
                      </TableRow>
                    ) : (
                      attendanceList.slice(0, 5).map((row, idx) => (
                        <TableRow key={row.timeAttendanceRequestID || idx} hover>
                          <TableCell style={{ fontWeight: "bold" }}>
                            {moment(row.workDate || row.checkInDT || row.checkOutDT).format("D MMM BB")}
                          </TableCell>
                          <TableCell style={{ fontSize: "13px" }}>
                            <span style={{ fontWeight: "500", color: "#007bff" }}>
                              {row.category?.categoryName || getAttendanceTypeText(row.attendanceType)}
                              {row.durationHours ? ` (${row.durationHours} ชม.)` : ""}
                            </span>
                          </TableCell>
                          <TableCell>{renderAttendanceStatus(row)}</TableCell>
                          <TableCell align="center">
                            <Button
                              color="info"
                              size="sm"
                              round
                              justIcon
                              title="ดูรายละเอียด / ติดตามสถานะ"
                              onClick={() => history.push(`/hr/attendance/request/${row.timeAttendanceRequestID}`)}
                            >
                              <i className="fa fa-eye"></i>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </GridItem>

        {/* รายการการลาล่าสุด */}
        <GridItem xs={12} sm={12} md={6}>
          <Card>
            <CardHeader color="primary">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>
                    <i className="fa fa-calendar-alt mr-2"></i> รายการขอลาล่าสุด
                  </h4>
                  <p className={classes.cardCategoryWhite}>ปีงบประมาณ {fiscalYear}</p>
                </div>
                <Button
                  color="transparent"
                  size="sm"
                  onClick={() => history.push("/leave/me")}
                  style={{ color: "#fff", border: "1px solid rgba(255,255,255,0.5)" }}
                >
                  ดูทั้งหมด
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <div className={classes.tableResponsive}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold" }}>ช่วงวันที่ลา</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>ประเภท / เหตุผล</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>จำนวน</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>สถานะ</TableCell>
                      <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>จัดการ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaveList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" style={{ color: "#888", padding: "20px" }}>
                          ไม่มีประวัติการลาในปีงบประมาณนี้
                        </TableCell>
                      </TableRow>
                    ) : (
                      leaveList.slice(0, 5).map((row, idx) => {
                        let statusChip = <Chip label="รออนุมัติ" className={classes.statusPending} size="small" />;
                        if (row.isCancel) {
                          statusChip = <Chip label="ยกเลิก" className={classes.statusCancelled} size="small" />;
                        } else if (row.isApprove === true) {
                          statusChip = <Chip label="อนุมัติแล้ว" className={classes.statusApproved} size="small" />;
                        } else if (row.isApprove === false) {
                          statusChip = <Chip label="ไม่อนุมัติ" className={classes.statusRejected} size="small" />;
                        }

                        return (
                          <TableRow key={row.leaveRequestID || idx} hover>
                            <TableCell style={{ fontWeight: "bold" }}>
                              {moment(row.startDate).format("D MMM BB")}
                              {row.startDate !== row.endDate && ` - ${moment(row.endDate).format("D MMM BB")}`}
                            </TableCell>
                            <TableCell style={{ fontSize: "13px", maxWidth: "160px" }}>
                              {row.title || row.leaveDesc || "-"}
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>
                              {row.totalDate} วัน
                            </TableCell>
                            <TableCell>{statusChip}</TableCell>
                            <TableCell align="center">
                              <Button
                                color="info"
                                size="sm"
                                round
                                justIcon
                                title="ดูรายละเอียดการลา"
                                onClick={() =>
                                  history.push({
                                    pathname: `/leave/id/${row.leaveRequestID}`,
                                    state: { leaveRequestID: row.leaveRequestID },
                                  })
                                }
                              >
                                <i className="fa fa-eye"></i>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ========================================================= */}
      {/* Dialog แสดงรายละเอียด Event จากปฏิทิน */}
      {/* ========================================================= */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: "#f5f5f5", padding: "14px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" style={{ fontWeight: "bold", fontFamily: '"Sarabun", sans-serif' }}>
              รายละเอียดการลา
            </Typography>
            <Button
              color="transparent"
              justIcon
              size="sm"
              onClick={() => setModalOpen(false)}
              style={{ margin: 0, padding: 0, color: "#666" }}
            >
              <Close />
            </Button>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "20px" }}>
          {selectedEvent && (
            <div>
              <div style={{ marginBottom: "12px" }}>
                <Typography variant="body2" color="textSecondary">ประเภทการลา:</Typography>
                <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#1976d2" }}>
                  {selectedEvent.typeName}
                </Typography>
              </div>
              <div style={{ marginBottom: "12px" }}>
                <Typography variant="body2" color="textSecondary">ช่วงวันที่ลา:</Typography>
                <Typography variant="body1" style={{ fontWeight: "bold" }}>
                  {moment(selectedEvent.startDate).format("D MMMM BBBB")}
                  {selectedEvent.startDate !== selectedEvent.endDate && ` ถึง ${moment(selectedEvent.endDate).format("D MMMM BBBB")}`}
                  {" "}({selectedEvent.totalDate} วัน)
                </Typography>
              </div>
              {selectedEvent.reason && (
                <div style={{ marginBottom: "12px" }}>
                  <Typography variant="body2" color="textSecondary">เหตุผล / รายละเอียด:</Typography>
                  <Typography variant="body1">{selectedEvent.reason}</Typography>
                </div>
              )}
              <div>
                <Typography variant="body2" color="textSecondary">สถานะคำขอ:</Typography>
                <div style={{ marginTop: "4px" }}>
                  {selectedEvent.isCancel ? (
                    <Chip label="ยกเลิกแล้ว" className={classes.statusCancelled} size="small" />
                  ) : selectedEvent.isApprove === true ? (
                    <Chip label="อนุมัติแล้ว" className={classes.statusApproved} size="small" />
                  ) : selectedEvent.isApprove === false ? (
                    <Chip label="ไม่อนุมัติ" className={classes.statusRejected} size="small" />
                  ) : (
                    <Chip label="รอการอนุมัติ" className={classes.statusPending} size="small" />
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px", backgroundColor: "#fafafa" }}>
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              setModalOpen(false);
              const targetId = selectedEvent?.leaveRequestID || selectedEvent?.raw?.leaveRequestID;
              if (targetId) {
                history.push({
                  pathname: `/leave/id/${targetId}`,
                  state: { leaveRequestID: targetId },
                });
              } else {
                history.push("/leave/me");
              }
            }}
          >
            <i className="fa fa-file-alt mr-1"></i> รายละเอียดทั้งหมด
          </Button>
          <Button color="secondary" size="sm" onClick={() => setModalOpen(false)}>
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HRDashboard;
